import { useEffect, useRef } from 'react'

/**
 * useBackButtonClose — wire a browser-back-button dismissal into a
 * dismissible UI layer (modal, sheet, overlay, lightbox, …).
 *
 * The PWA normally runs at a single URL, so pressing the hardware /
 * browser back button used to unload the tab — a surprise for anyone
 * coming from a native app where "back" closes the topmost layer.
 * This hook teaches each layer to behave that way:
 *
 *   1. The moment `open` flips false → true, we push a tagged history
 *      entry via `history.pushState({ govuBackKey: <unique id> }, '')`.
 *      The tag lets us tell our entries apart from real page history
 *      entries the app (or embedded links) might create.
 *
 *   2. We maintain a module-level LIFO stack of every active entry
 *      across all hook instances. A single shared `popstate` listener
 *      pops the top entry when the browser navigates back — this is
 *      the "topmost layer closes first" guarantee. Deep nesting
 *      (detail modal → person modal → lightbox) falls out naturally
 *      because each layer pushes its own entry and they get popped in
 *      reverse order.
 *
 *   3. When `open` flips true → false by user action other than the
 *      back button (X button, Escape key, backdrop click), we call
 *      `history.back()` to pop the entry we own so the address bar
 *      stays clean. The stack entry is removed first, so the
 *      resulting popstate is a no-op.
 *
 *   4. On unmount we conservatively `replaceState` over our entry
 *      rather than calling `history.back()` synchronously — the
 *      latter races React's render cycle and can close the wrong
 *      layer. Orphaned entries are harmless; they just become
 *      regular navigation history.
 *
 * Edge cases handled:
 *   - Initial mount with `open === true` does NOT push (we only push
 *     on a false → true transition) — this keeps refreshes or parent
 *     resets from injecting stale entries.
 *   - A reload with a modal open: on boot, state is reset to closed,
 *     so there's no mismatch even if a stale entry lingers in
 *     history.
 *   - User navigates away via a link: cleanup on unmount wipes our
 *     marker without touching the history stack.
 *
 * @param open     Whether the layer is currently visible.
 * @param onClose  Called when the back button fires while this layer
 *                 was the topmost pushed entry.
 */
export function useBackButtonClose(open: boolean, onClose: () => void): void {
  // Stable identity for this hook's entry. `useRef` so it persists
  // across renders but never changes.
  const keyRef = useRef<string>('')
  if (!keyRef.current) {
    keyRef.current = `govu-back-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 10)}`
  }

  // Latest onClose reference — the stack holds this ref so swapping
  // the callback between renders doesn't require re-pushing history.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Previous `open` value so we can detect transitions rather than
  // reacting to every render. Initialised lazily from the first
  // value we see so a hook that mounts with `open === true` does NOT
  // treat that initial render as a false → true transition. This
  // matches the spec: we only push when the user opens the layer
  // during the session, not when the parent boots it already open.
  const prevOpenRef = useRef<boolean | null>(null)

  // Whether this hook currently owns a history entry on the shared
  // stack. Drives the "clean up on manual close / unmount" branches.
  const isPushedRef = useRef<boolean>(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const prev = prevOpenRef.current
    prevOpenRef.current = open

    // First run after mount: record the current value and don't do
    // anything. This prevents a modal that mounts already-open from
    // injecting a back-button entry the user didn't explicitly open.
    if (prev === null) return

    if (!prev && open) {
      // false → true: push our entry onto both browser history and
      // the shared stack, then ensure the module listener is live.
      try {
        window.history.pushState({ govuBackKey: keyRef.current }, '')
      } catch {
        // Some sandboxed environments block pushState. Bail silently
        // so the feature degrades gracefully.
        return
      }
      pushEntry(keyRef.current, onCloseRef, isPushedRef)
      isPushedRef.current = true
      ensureListener()
      return
    }

    if (prev && !open && isPushedRef.current) {
      // true → false by user action: remove ourselves from the stack
      // first so the popstate that `history.back()` triggers sees no
      // owner and fires no onClose. Only call history.back() if our
      // entry is actually on top of the browser stack (defensive —
      // if another layer somehow pushed after us, replaceState the
      // current entry clean and let the stack sort itself out).
      const wasOnTop = removeEntry(keyRef.current)
      isPushedRef.current = false

      const currentKey =
        (window.history.state as { govuBackKey?: string } | null)?.govuBackKey
      if (wasOnTop && currentKey === keyRef.current) {
        try {
          window.history.back()
        } catch {
          // ignore
        }
      } else if (currentKey === keyRef.current) {
        // Our marker is still the top entry but we weren't tracked —
        // wipe the marker so future navigation isn't polluted.
        try {
          window.history.replaceState(null, '')
        } catch {
          // ignore
        }
      }
    }
  }, [open])

  // Unmount cleanup: if our entry is still live, drop it from the
  // shared stack and wipe the history marker in place. We avoid
  // `history.back()` here because it can race an unrelated React
  // update.
  useEffect(() => {
    return () => {
      if (!isPushedRef.current) return
      removeEntry(keyRef.current)
      isPushedRef.current = false

      if (typeof window === 'undefined') return
      const currentKey =
        (window.history.state as { govuBackKey?: string } | null)?.govuBackKey
      if (currentKey === keyRef.current) {
        try {
          window.history.replaceState(null, '')
        } catch {
          // ignore
        }
      }
    }
  }, [])
}

// ————————————————————————————————————————————————————————————————
// Module-level shared stack + popstate listener
// ————————————————————————————————————————————————————————————————

interface StackEntry {
  key: string
  onCloseRef: { current: () => void }
  // Owner hook's `isPushedRef` — cleared by the popstate handler so
  // the owning hook's next effect run doesn't try to manually-close
  // an entry that's already gone.
  isPushedRef: { current: boolean }
}

const entryStack: StackEntry[] = []
let listenerAttached = false

function pushEntry(
  key: string,
  onCloseRef: { current: () => void },
  isPushedRef: { current: boolean }
): void {
  entryStack.push({ key, onCloseRef, isPushedRef })
}

/**
 * Remove an entry from the shared stack. Returns true if the removed
 * entry was on top (i.e. the most recently pushed) — the caller uses
 * this to decide whether `history.back()` is safe.
 */
function removeEntry(key: string): boolean {
  const topIndex = entryStack.length - 1
  if (topIndex < 0) return false
  if (entryStack[topIndex].key === key) {
    entryStack.pop()
    return true
  }
  // Not on top — rare but possible if a sibling unmounts out of order.
  const idx = entryStack.findIndex((e) => e.key === key)
  if (idx >= 0) {
    entryStack.splice(idx, 1)
  }
  return false
}

function ensureListener(): void {
  if (listenerAttached) return
  if (typeof window === 'undefined') return
  window.addEventListener('popstate', handlePopState)
  listenerAttached = true
}

function handlePopState(event: PopStateEvent): void {
  // Browser popped one entry. If our top-of-stack entry is not the
  // key we're now sitting on, the popped entry was ours — fire its
  // onClose. Otherwise the user navigated past an entry we don't own
  // (regular page history) and we stay out of the way.
  const top = entryStack[entryStack.length - 1]
  if (!top) return

  const currentKey =
    (event.state as { govuBackKey?: string } | null)?.govuBackKey ?? null
  if (currentKey === top.key) {
    // We're still sitting on our top entry — user navigated forward
    // onto an entry that coincidentally has the same key (shouldn't
    // happen, keys are unique, but guard anyway).
    return
  }

  // Pop the stack entry, flip its owner hook's "is pushed" flag, and
  // dispatch the onClose. Clearing the ref BEFORE onClose fires is
  // critical — onClose will flip React state which re-runs the
  // hook's effect, and that effect must not try to `history.back()`
  // an entry we've already popped.
  entryStack.pop()
  top.isPushedRef.current = false
  try {
    top.onCloseRef.current()
  } catch (err) {
    // Keep the listener healthy even if an onClose throws.
    // eslint-disable-next-line no-console
    console.error('[useBackButtonClose] onClose threw', err)
  }
}
